/**
 * CustomerDemoMode — Interactive "Demo Mode" director (Customer, Phase 4A).
 *
 * NOT a simulator. Drives the REAL customer journey on the isolated demo tenant:
 * a real RemodConnect request is staged + an OTP issued (SMS suppressed → shown
 * in a Demo SMS card), verified, a real customer + portal session created, and
 * the lead routed through the REAL distribution to the demo provider. The
 * director then opens the REAL Customer Portal (/remodify/{slug}) and finally the
 * REAL Provider Inbox (/marketplace/inbox) to PROVE the same request_id arrived.
 *
 * Mounted once in the App shell. Activates on `customerdemo:start` or when
 * localStorage `demo_mode === 'customer'`. Provider Demo behavior is untouched.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, Play, Pause, SkipForward, RotateCcw, X, ArrowRight,
  AlertTriangle, Smartphone, Mail, CheckCircle2, CalendarClock,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// localStorage keys the customer demo writes (provider login for the inbox proof
// + the customer portal session + the demo session itself). All cleared on exit.
const DEMO_LS = ['demo_mode', 'demo_session', 'token', 'company_id', 'user_id',
  'user_name', 'user_role', 'analytics_user', 'enabled_features',
  'hasCompletedOnboarding', 'first_login_complete',
  'remodify_token', 'remodify_slug', 'remodify_name'];

/** Boot the Customer Demo: reset the demo tenant, prime the demo-provider login
 *  (so the inbox proof screen loads), enter the neutral demo backdrop. */
export async function launchCustomerDemo(navigate) {
  const r = await fetch(`${API}/api/demo/session/customer`, { method: 'POST' });
  if (!r.ok) throw new Error('customer demo session failed');
  const s = await r.json();
  // Prime the demo-provider login so the final /marketplace/inbox proof renders.
  localStorage.setItem('token', s.provider_token);
  localStorage.setItem('company_id', s.company_id);
  localStorage.setItem('user_id', s.account_id);
  localStorage.setItem('user_name', s.provider_name);
  localStorage.setItem('user_role', 'admin');
  localStorage.setItem('analytics_user', JSON.stringify({
    id: s.account_id, company_id: s.company_id, name: s.provider_name,
    role: 'admin', email: 'demo-provider@remoddash.com',
  }));
  localStorage.setItem('enabled_features', JSON.stringify(s.enabled_features || []));
  localStorage.setItem('hasCompletedOnboarding', 'true');
  localStorage.setItem('first_login_complete', 'true');
  localStorage.setItem('demo_session', JSON.stringify(s));
  localStorage.setItem('demo_mode', 'customer');
  window.dispatchEvent(new Event('customerdemo:start'));
  navigate('/__demo');
}

const CustomerDemoMode = () => {
  const navigate = useNavigate();
  const [active, setActive] = useState(() => localStorage.getItem('demo_mode') === 'customer');
  const [session, setSession] = useState(() => {
    try { return JSON.parse(localStorage.getItem('demo_session') || 'null'); } catch { return null; }
  });
  const [idx, setIdx] = useState(0);
  const [state, setState] = useState({});
  const [playing, setPlaying] = useState(true);
  const [finished, setFinished] = useState(false);
  const [chars, setChars] = useState(0);
  const [narr, setNarr] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const advanceRef = useRef(null);
  const typeRef = useRef(null);
  const ranRef = useRef(-1);
  const stateRef = useRef({});

  const steps = session?.steps || [];
  const step = steps[idx];

  const clearTimers = useCallback(() => {
    if (advanceRef.current) clearTimeout(advanceRef.current);
    if (typeRef.current) clearInterval(typeRef.current);
  }, []);

  useEffect(() => {
    const onStart = () => {
      try { setSession(JSON.parse(localStorage.getItem('demo_session') || 'null')); } catch { /* */ }
      setIdx(0); setState({}); stateRef.current = {}; setFinished(false);
      setPlaying(true); setError(null); setSummary(null); ranRef.current = -1; setActive(true);
    };
    window.addEventListener('customerdemo:start', onStart);
    return () => window.removeEventListener('customerdemo:start', onStart);
  }, []);

  useEffect(() => {
    if (active && session) document.body.setAttribute('data-demo', 'customer');
    else document.body.removeAttribute('data-demo');
    return () => document.body.removeAttribute('data-demo');
  }, [active, session]);

  const exit = useCallback(async () => {
    clearTimers();
    try { await fetch(`${API}/api/demo/session/customer/end`, { method: 'POST' }); } catch { /* */ }
    DEMO_LS.forEach((k) => localStorage.removeItem(k));
    setActive(false);
    navigate('/');
  }, [clearTimers, navigate]);

  const typeNarration = useCallback((text, dur) => {
    setNarr(text); setChars(0);
    if (typeRef.current) clearInterval(typeRef.current);
    const total = text.length || 1;
    const ms = Math.max(16, (dur - 1800) / total);
    let c = 0;
    typeRef.current = setInterval(() => { c += 1; setChars(c); if (c >= total) clearInterval(typeRef.current); }, ms);
  }, []);

  // Director: run the current step against the REAL backend, then navigate the
  // visitor into the real Customer Portal / Provider Inbox at the right beats.
  useEffect(() => {
    if (!active || finished || !step || !session) return undefined;
    if (ranRef.current === idx) return undefined;
    ranRef.current = idx;
    setBusy(true); setError(null); setSummary(null);
    (async () => {
      try {
        const r = await fetch(`${API}/api/demo/run/customer/${idx}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ state: stateRef.current }),
        });
        const data = await r.json();
        if (!data.success) { setError(data.error || 'This step hit a real error.'); setBusy(false); return; }
        stateRef.current = data.state || stateRef.current;
        setState(stateRef.current);
        setSummary(data.summary || null);

        // After verify, prime the REAL customer portal session for the next step.
        if (step.id === 'verify' && stateRef.current.slug) {
          localStorage.setItem('remodify_token', stateRef.current.portal_token || '');
          localStorage.setItem('remodify_slug', stateRef.current.slug);
          localStorage.setItem('remodify_name', session.customer_name || 'Customer');
        }

        // Navigate to the real screen for portal / inbox beats; the request +
        // verify beats stay on the neutral demo backdrop with the SMS/email cards.
        let target = null;
        if (step.page === 'portal' && stateRef.current.slug) target = `/remodify/${stateRef.current.slug}`;
        else if (step.page === 'inbox') target = '/marketplace/inbox';
        if (target) {
          navigate('/__demo', { replace: true });
          setTimeout(() => navigate(target), 90);
        }
        setBusy(false);
      } catch (e) {
        setError(String(e?.message || e)); setBusy(false);
      }
    })();
    return undefined;
  }, [active, finished, idx, step, session, navigate]);

  useEffect(() => {
    if (!active || finished || !step || busy || error) return undefined;
    clearTimers();
    const dur = Math.min(12000, Math.max(7000, (step.narration || '').length * 66));
    if (!playing) { setNarr(step.narration); setChars(step.narration.length); return undefined; }
    typeNarration(step.narration, dur);
    advanceRef.current = setTimeout(() => {
      if (idx < steps.length - 1) setIdx((i) => i + 1);
      else { setFinished(true); setPlaying(false); }
    }, dur);
    return clearTimers;
  }, [active, finished, step, busy, error, playing, idx, steps.length, clearTimers, typeNarration]);

  const skip = () => {
    if (busy) return;
    clearTimers();
    if (idx < steps.length - 1) setIdx((i) => i + 1);
    else { setFinished(true); setPlaying(false); }
  };
  const restart = async () => {
    clearTimers(); setBusy(true);
    try { await launchCustomerDemo(navigate); } catch { setError('Could not restart demo.'); }
    setBusy(false);
  };

  if (!active || !session) return null;

  const ACCENT = '#A855F7';

  return (
    <>
      {/* Persistent DEMO banner */}
      <div className="fixed top-0 left-0 right-0 z-[10000] overflow-hidden flex items-center justify-center gap-1.5 sm:gap-2 px-2 sm:px-4 py-1.5 text-white text-[10px] sm:text-sm font-semibold"
        style={{ background: 'linear-gradient(90deg,#2e1065,#6d28d9,#2e1065)', paddingTop: 'calc(0.375rem + env(safe-area-inset-top, 0px))' }}
        data-testid="customer-demo-banner">
        <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-purple-300 shrink-0" />
        <span className="whitespace-nowrap">DEMO MODE — Customer journey · sample data · no charges</span>
        <button onClick={exit} data-testid="customer-demo-exit-top"
          className="ml-1 sm:ml-3 shrink-0 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-white/15 hover:bg-white/25 transition-colors text-[10px] sm:text-[11px]">
          <X className="w-3 h-3" /> Exit
        </button>
      </div>

      {/* Ari director dock */}
      <div className="fixed bottom-0 left-0 right-0 z-[10000] px-3 sm:px-5 pt-2"
        style={{ background: 'linear-gradient(180deg, rgba(24,10,48,0) 0%, rgba(24,10,48,0.9) 35%)', paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}
        data-testid="customer-demo-director">
        <div className="max-w-5xl mx-auto rounded-2xl px-4 py-3"
          style={{ background: 'rgba(28,14,64,0.92)', border: '1px solid rgba(168,85,247,0.4)', backdropFilter: 'blur(10px)', boxShadow: '0 -8px 40px -10px rgba(0,0,0,0.5)' }}>
          {/* progress */}
          <div className="flex gap-1 mb-2.5" data-testid="customer-demo-stepper">
            {steps.map((s, i) => (
              <div key={s.id} className="h-1 flex-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.12)' }}>
                <div className="h-full rounded-full transition-all duration-300"
                  style={{ width: i <= idx || finished ? '100%' : '0%', background: ACCENT, opacity: (i < idx || finished) ? 0.6 : i === idx ? 1 : 0 }} />
              </div>))}
          </div>

          {/* Real step result cards (SMS OTP · email portal link · inbox proof) */}
          {!finished && !error && summary && (
            <div className="mb-2.5 flex flex-wrap gap-2" data-testid="customer-demo-summary">
              {summary.otp && (
                <div className="rounded-xl px-3 py-2 min-w-[180px]" style={{ background: 'rgba(168,85,247,0.14)', border: '1px solid rgba(168,85,247,0.35)' }} data-testid="demo-sms-card">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-purple-200 mb-1">
                    <Smartphone className="w-3 h-3" /> Demo SMS · {summary.sms_to}
                  </div>
                  <div className="text-[12px] text-purple-100/90 leading-snug">{summary.sms_preview}</div>
                  <div className="mt-1 text-white font-extrabold tracking-[0.3em] text-lg" data-testid="demo-sms-otp">{summary.otp}</div>
                </div>
              )}
              {Array.isArray(summary.windows) && summary.windows.length > 0 && summary.otp && (
                <div className="rounded-xl px-3 py-2" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(168,85,247,0.25)' }} data-testid="demo-windows-card">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-purple-200 mb-1">
                    <CalendarClock className="w-3 h-3" /> 3 preferred windows
                  </div>
                  {summary.windows.map((w, i) => (
                    <div key={i} className="text-[11px] text-purple-100/90">{i + 1}. {w}</div>
                  ))}
                </div>
              )}
              {summary.portal_link && summary.email_to && (
                <div className="rounded-xl px-3 py-2 min-w-[200px]" style={{ background: 'rgba(59,130,246,0.14)', border: '1px solid rgba(96,165,250,0.35)' }} data-testid="demo-email-card">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-blue-200 mb-1">
                    <Mail className="w-3 h-3" /> Demo Email · {summary.email_to}
                  </div>
                  <div className="text-[12px] text-blue-100/90 leading-snug">{summary.email_preview}</div>
                  <div className="mt-1 text-[11px] text-blue-200 underline truncate" data-testid="demo-email-link">{summary.portal_link}</div>
                </div>
              )}
              {typeof summary.inbox_match === 'boolean' && (
                <div className="rounded-xl px-3 py-2 min-w-[220px]" style={{ background: summary.inbox_match ? 'rgba(16,185,129,0.16)' : 'rgba(245,158,11,0.16)', border: `1px solid ${summary.inbox_match ? 'rgba(16,185,129,0.4)' : 'rgba(245,158,11,0.4)'}` }} data-testid="demo-proof-card">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: summary.inbox_match ? '#6EE7B7' : '#FCD34D' }}>
                    <CheckCircle2 className="w-3 h-3" /> {summary.inbox_match ? 'Provider received the same request' : 'Routing…'}
                  </div>
                  <div className="text-[11px] text-white/90">Request <span className="font-mono" data-testid="demo-proof-request-id">#{String(summary.request_id || '').slice(0, 8)}</span> → {summary.provider_name}</div>
                </div>
              )}
            </div>
          )}

          {finished ? (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3" data-testid="customer-demo-end">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${ACCENT}33` }}>
                  <Sparkles className="w-5 h-5 text-purple-300" />
                </div>
                <div className="text-white text-sm sm:text-[15px]">
                  <span className="font-bold">One request, end to end.</span> Maria booked, got her portal, and the provider received it — automatically.
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => { DEMO_LS.forEach((k) => localStorage.removeItem(k)); setActive(false); navigate('/landing'); }}
                  data-testid="customer-demo-cta"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-bold text-white text-sm transition-transform hover:scale-105"
                  style={{ background: ACCENT }}>
                  Sign up free <ArrowRight className="w-4 h-4" />
                </button>
                <button onClick={restart} data-testid="customer-demo-replay" className="px-3 py-2 rounded-full text-sm text-white/85 hover:bg-white/10 transition-colors inline-flex items-center gap-1.5">
                  <RotateCcw className="w-4 h-4" /> Again
                </button>
                <button onClick={exit} data-testid="customer-demo-exit" className="px-3 py-2 rounded-full text-sm text-white/70 hover:bg-white/10 transition-colors">Exit</button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 demo-ari-float" style={{ background: `${ACCENT}33`, boxShadow: `0 0 14px ${ACCENT}55` }}>
                <Sparkles className="w-5 h-5 text-purple-300" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-purple-300">
                  Ari · Step {idx + 1} of {steps.length}{step ? ` · ${step.title}` : ''}{busy ? ' · working…' : ''}
                </div>
                {error ? (
                  <div className="text-amber-300 text-sm flex items-center gap-1.5" data-testid="customer-demo-step-error">
                    <AlertTriangle className="w-4 h-4" /> Real step error: {error}
                  </div>
                ) : (
                  <div className="text-white text-sm sm:text-[15px] leading-snug" data-testid="customer-demo-narration">
                    {narr.slice(0, chars)}{chars < narr.length && <span className="inline-block w-1.5 h-4 ml-0.5 align-middle demo-caret" style={{ background: ACCENT }} />}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button onClick={() => setPlaying((p) => !p)} data-testid="customer-demo-play-pause"
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-semibold text-white transition-transform hover:scale-105" style={{ background: ACCENT }}>
                  {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}<span className="hidden sm:inline">{playing ? 'Pause' : 'Play'}</span>
                </button>
                <button onClick={skip} disabled={busy} data-testid="customer-demo-skip" className="p-2 rounded-full text-white/80 hover:bg-white/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" aria-label="Skip"><SkipForward className="w-4 h-4" /></button>
                <button onClick={restart} data-testid="customer-demo-restart" className="p-2 rounded-full text-white/80 hover:bg-white/10 transition-colors" aria-label="Restart"><RotateCcw className="w-4 h-4" /></button>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        body[data-demo="customer"] [data-testid="global-portal-viewmode-toggle"],
        body[data-demo="customer"] [data-testid="view-mode-guidance-popover"],
        body[data-demo="customer"] [data-testid="business-setup-banner"],
        body[data-demo="customer"] [data-testid="mobile-footer-nav"],
        body[data-demo="customer"] [data-testid="ari-activation-nudge"],
        body[data-demo="customer"] [data-testid="ari-upgrade-card"],
        body[data-demo="customer"] [data-testid="feedback-widget-fab"] { display: none !important; }
        @media (max-width: 640px) {
          body[data-demo="customer"] { padding-top: calc(34px + env(safe-area-inset-top, 0px)) !important; }
        }
      `}</style>
    </>
  );
};

export default CustomerDemoMode;
